import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Footer from "../Footer/Footer";
import {
  getSavedDataFromLocalDB,
  getProductCollections,
  getCollectionsForProduct,
  removeProductFromCollection,
} from "../../utils/storeLocalDB";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../utils/cartContext";
import { Helmet } from "react-helmet";
import { url } from "../../Constant/TextConstant";
import { useAuth } from "../../utils/AuthContext";
import {
  FaHeart,
  FaRegHeart,
  FaShoppingCart,
  FaStar,
  FaFilter,
} from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Header from "../Header";
import { useUserLocation } from "../../utils/useUserLocation";

// Styled components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const ContentContainer = styled.main`
  flex: 1;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #3498db;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 1rem;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImage = styled.div`
  height: 220px;
  background-color: #f5f5f5;
  background-image: ${(props) =>
    props.$imageUrl ? `url(${props.$imageUrl})` : "none"};
  background-size: cover;
  background-position: center;
  position: relative;

  @media (max-width: 768px) {
    height: 160px;
  }
`;

const ProductBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: ${(props) =>
    props.$type === "discount" ? "#e74c3c" : "#2ecc71"};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const WishlistButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${(props) => (props.$isSaved ? "#e74c3c" : "#95a5a6")};

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    color: #e74c3c;
  }
`;

const ProductDetails = styled.div`
  padding: 1.25rem;
`;

const ProductName = styled.h3`
  font-size: 1rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const AddToCartButton = styled.button`
  width: 100%;
  background-color: #2ecc71;
  color: white;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #27ae60;
  }

  svg {
    font-size: 1rem;
  }
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const CurrentPrice = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
`;

const OriginalPrice = styled.span`
  font-size: 0.9rem;
  color: #95a5a6;
  text-decoration: line-through;
`;

const DiscountPercentage = styled.span`
  font-size: 0.8rem;
  color: #e74c3c;
  font-weight: 600;
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
`;

const RatingStars = styled.div`
  color: #f39c12;
  display: flex;
  align-items: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  grid-column: 1 / -1;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: #bdc3c7;
  margin-bottom: 1rem;
`;

const EmptyStateText = styled.p`
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 1.5rem;
`;

const ExploreButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #2980b9;
  }
`;

const CollectionSelector = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const CollectionButton = styled.button`
  padding: 0.5rem 1.25rem;
  background-color: ${(props) => (props.$active ? "#3498db" : "#ecf0f1")};
  color: ${(props) => (props.$active ? "white" : "#2c3e50")};
  border: none;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: ${(props) => (props.$active ? "#2980b9" : "#dfe6e9")};
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SortSelect = styled.select`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid #ddd;
  background-color: white;
  font-size: 0.9rem;
  cursor: pointer;
  outline: none;
`;

const CollectionTag = styled.span`
  display: inline-block;
  background-color: #e8f4fc;
  color: #3498db;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

function SavedProductsPage() {
  const [savedProducts, setSavedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("all");
  const [sortOption, setSortOption] = useState("recent");
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { currentUser} = useAuth();
  const { location: currentLocation, updateLocation } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  const [userLocation, setUserLocation] = useState({
    coordinates: { latitude: 0, longitude: 0 },
    address: "",
    details: null,
  });
 useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (currentLocation) {
      setUserLocation({
        coordinates: {
          latitude: currentLocation.coordinates.latitude,
          longitude: currentLocation.coordinates.longitude,
        },
        address: currentLocation.address || "",
        details: currentLocation.details || null,
      });
    }
  }, [currentLocation]);

  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  const createProductSlug = (productName) => {
    return (
      productName
        ?.toLowerCase()
        ?.trim()
        ?.replace(/%/g, "-percent-")
        ?.replace(/\+/g, "-plus-")
        ?.replace(/[^\w\s-]/g, "")
        ?.replace(/\s+/g, "-")
        ?.replace(/-+/g, "-")
        ?.slice(0, 60)
        ?.replace(/-$/, "") || ""
    );
  };

  const handleProductClick = (product) => {
    const slug = createProductSlug(product.productName || product.ProductName);
    navigate(`/product/${product.id || product.ID}/${slug}`, {
      state: { product },
    });
  };

  const handleAddToCart = (product, e) => {
     if (product.StoreMobile === currentUser) {
       const userConfirmed = window.confirm(
         "This is your own product. You can't add it to the cart."
       );
       // You could do something with userConfirmed if needed.
       return;
     }
    e?.stopPropagation();
    const productToAdd = {
      ID: product.id || product.ID || 0,
      ProductName: product.productName || product.ProductName || "",
      ProductCode: product.productCode || product.ProductCode || "",
      OurPrice: product.ourPrice || product.OurPrice || 0,
      Mrp: product.mrp || product.Mrp || 0,
      DiscountPercentage: product.discount || product.Discount || 0,
      Image1: product.image1 || product.Image1 || "",
      Image2: product.image2 || product.Image2 || "",
      StoreMobile: product.storeMobile || product.StoreMobile || "",
      StoreName: product.storeName || product.StoreName || "",
      Category: product.category || product.Category || "",
      Size: product.size || product.Size || "",
      quantity: 1,
      Total: (product.ourPrice || product.OurPrice || 0) * 1,
      Latitude: product.latitude || product.Latitude || 0,
      Longitude: product.longitude || product.Longitude || 0,
      AverageRating: product.averageRating || product.AverageRating || 0,
      stock: 10,
      addedAt: new Date().toISOString(),
    };
    addToCart(productToAdd);
  };

  const sortProducts = (products, option) => {
    const sorted = [...products];
    switch (option) {
      case "price-low":
        return sorted.sort((a, b) => (a.ourPrice || 0) - (b.ourPrice || 0));
      case "price-high":
        return sorted.sort((a, b) => (b.ourPrice || 0) - (a.ourPrice || 0));
      case "rating":
        return sorted.sort(
          (a, b) => (b.averageRating || 0) - (a.averageRating || 0)
        );
      case "discount":
        return sorted.sort((a, b) => {
          const discountA = a.mrp
            ? ((a.mrp - (a.ourPrice || 0)) / a.mrp) * 100
            : 0;
          const discountB = b.mrp
            ? ((b.mrp - (b.ourPrice || 0)) / b.mrp) * 100
            : 0;
          return discountB - discountA;
        });
      case "recent":
      default:
        return products;
    }
  };

  const loadProducts = () => {
    try {
      const savedData = getSavedDataFromLocalDB();
      const productCollections = savedData.productCollections || {};
      const collectionNames = Object.keys(productCollections);
      setCollections(collectionNames);

      let products = [];
      if (selectedCollection === "all") {
        const productMap = new Map();
        collectionNames.forEach((collectionName) => {
          productCollections[collectionName]?.products?.forEach((product) => {
            const key = `${product.ID}_${product.StoreMobile}`;
            if (!productMap.has(key)) {
              productMap.set(key, {
                ...product,
                id: product.ID,
                collections: getCollectionsForProduct(
                  product.ID,
                  product.StoreMobile
                ),
              });
            }
          });
        });
        products = Array.from(productMap.values());
      } else if (productCollections[selectedCollection]) {
        products = productCollections[selectedCollection].products.map(
          (product) => ({
            ...product,
            id: product.ID,
            collections: [selectedCollection],
          })
        );
      }

      setSavedProducts(sortProducts(products, sortOption));
    } catch (error) {
      setSavedProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [selectedCollection, sortOption]);

  const toggleSaveProduct = (productId, storeMobile, e) => {
    e?.stopPropagation();
    if (selectedCollection === "all") {
      Object.keys(getSavedDataFromLocalDB().productCollections || {}).forEach(
        (collectionName) => {
          removeProductFromCollection(productId, storeMobile, collectionName);
        }
      );
    } else {
      removeProductFromCollection(productId, storeMobile, selectedCollection);
    }
    loadProducts();
  };

  if (isLoading) {
    return (
      <PageContainer>
        <ContentContainer>
          <Title>Your Saved Products</Title>
          <FilterSection>
            <CollectionSelector>
              <Skeleton width={100} height={36} borderRadius={20} />
              <Skeleton width={80} height={36} borderRadius={20} />
              <Skeleton width={90} height={36} borderRadius={20} />
            </CollectionSelector>
            <Skeleton width={150} height={36} borderRadius={20} />
          </FilterSection>
          <ProductsGrid>
            {[...Array(6)].map((_, index) => (
              <SkeletonCard key={index}>
                <Skeleton height={220} />
                <div style={{ padding: "1.25rem" }}>
                  <Skeleton count={2} />
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </div>
              </SkeletonCard>
            ))}
          </ProductsGrid>
        </ContentContainer>
        <Footer />
      </PageContainer>
    );
  }

  return (
    <>
      <Helmet>
        <title>Your Saved Products | MyStore</title>
        <meta
          name="description"
          content="Browse and manage your saved products and collections."
        />
      </Helmet>

      <Header
        hasShowOptions={true}
        userLocation={userLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <PageContainer>
        <ContentContainer>
          <Title>Your Saved Products</Title>

          <FilterSection>
            {collections.length > 0 && (
              <CollectionSelector>
                <CollectionButton
                  $active={selectedCollection === "all"}
                  onClick={() => setSelectedCollection("all")}
                >
                  <FaFilter /> All
                </CollectionButton>
                {collections.map((collection) => (
                  <CollectionButton
                    key={collection}
                    $active={selectedCollection === collection}
                    onClick={() => setSelectedCollection(collection)}
                  >
                    {collection}
                  </CollectionButton>
                ))}
              </CollectionSelector>
            )}

            <SortSelect
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="recent">Recently Added</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="rating">Highest Rated</option>
              <option value="discount">Best Discount</option>
            </SortSelect>
          </FilterSection>

          {savedProducts.length > 0 ? (
            <ProductsGrid>
              {savedProducts.map((product) => (
                <ProductCard
                  key={`${product.id}_${product.StoreMobile}`}
                  onClick={() => handleProductClick(product)}
                >
                  <ProductImage
                    $imageUrl={
                      product.Image1
                        ? `${url.productImageStartingPath}/${product.Image1}`
                        : "/assets/images/default-product.png"
                    }
                  >
                    {product.Mrp && product.Mrp > product.OurPrice && (
                      <ProductBadge $type="discount">
                        {Math.round(
                          ((product.Mrp - product.OurPrice) / product.Mrp) * 100
                        )}
                        % OFF
                      </ProductBadge>
                    )}
                    <WishlistButton
                      $isSaved={true}
                      onClick={(e) =>
                        toggleSaveProduct(product.id, product.StoreMobile, e)
                      }
                    >
                      <FaHeart />
                    </WishlistButton>
                  </ProductImage>
                  <ProductDetails>
                    <ProductName>{product.ProductName}</ProductName>

                    {selectedCollection === "all" && product.collections && (
                      <div>
                        {product.collections.map((collection) => (
                          <CollectionTag key={collection}>
                            {collection}
                          </CollectionTag>
                        ))}
                      </div>
                    )}

                    <ProductPrice>
                      <CurrentPrice>₹{product.OurPrice}</CurrentPrice>
                      {product.Mrp && product.Mrp > product.OurPrice && (
                        <>
                          <OriginalPrice>₹{product.Mrp}</OriginalPrice>
                          <DiscountPercentage>
                            (
                            {Math.round(
                              ((product.Mrp - product.OurPrice) / product.Mrp) *
                                100
                            )}
                            % off)
                          </DiscountPercentage>
                        </>
                      )}
                    </ProductPrice>

                    <ProductRating>
                      <RatingStars>
                        <FaStar />
                        <span>{product.AverageRating || "0.0"}</span>
                      </RatingStars>
                    </ProductRating>

                    <AddToCartButton
                      onClick={(e) => handleAddToCart(product, e)}
                    >
                      <FaShoppingCart /> Add to Cart
                    </AddToCartButton>
                  </ProductDetails>
                </ProductCard>
              ))}
            </ProductsGrid>
          ) : (
            <EmptyState>
              <EmptyStateIcon>
                <FaRegHeart size={48} />
              </EmptyStateIcon>
              <EmptyStateText>
                {selectedCollection === "all"
                  ? "You haven't saved any products yet"
                  : `No products saved in "${selectedCollection}" collection`}
              </EmptyStateText>
              <ExploreButton onClick={() => navigate("/")}>
                Explore Products
              </ExploreButton>
            </EmptyState>
          )}
        </ContentContainer>
        <Footer />
      </PageContainer>
    </>
  );
}

export default SavedProductsPage;
